/**
 * Project Service - API Client for Carousel Projects
 *
 * Handles saving, loading, listing, and deleting projects via the backend API.
 * All operations go through the backend API (no direct Supabase calls).
 */

import { apiGet, apiPost } from '@/services/apiClient';
import { Slide, Profile, CarouselStyle, AspectRatio, Theme, FontStyle, LayoutSettings, CarouselProject, AnalisePreCriacao, ChecklistValidacao } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface ProjectSummary {
  id: string;
  name: string;
  style: CarouselStyle;
  aspectRatio: AspectRatio;
  slideCount: number;
  thumbnailUrl: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// SAVE PROJECT
// ============================================================================

// ----------------------------------------------------------------------------
// Save serialization (per project id)
//
// Saves of the SAME project must never run concurrently. A slow multi-MB
// upload (large carousels, mobile) can still be in flight when the next
// debounced auto-save or thumbnail save fires; two concurrent upserts then
// contend for the same cai_projects row lock and one hits the Postgres
// statement timeout -> "Erro ao salvar projeto" (seen mostly on iPhone during
// export, where several saves fire close together). We keep at most one save
// in flight per id plus one trailing save (latest payload wins); callers that
// arrive mid-flight resolve with the trailing save's result.
// ----------------------------------------------------------------------------
type SavePayload = { id: string;[key: string]: any };
type SaveWaiter = { resolve: (id: string) => void; reject: (e: any) => void };
const saveInFlight = new Set<string>();
const savePending = new Map<string, { payload: SavePayload; waiters: SaveWaiter[] }>();

async function runSave(id: string, payload: SavePayload): Promise<string> {
  saveInFlight.add(id);
  try {
    const data = await apiPost<{ id: string }>('/api/projects/save', { project: payload });
    return data.id;
  } finally {
    saveInFlight.delete(id);
    const next = savePending.get(id);
    if (next) {
      savePending.delete(id);
      // Run the coalesced trailing save and settle everyone waiting on it.
      runSave(id, next.payload).then(
        (savedId) => next.waiters.forEach((w) => w.resolve(savedId)),
        (err) => next.waiters.forEach((w) => w.reject(err)),
      );
    }
  }
}

function enqueueSave(payload: SavePayload): Promise<string> {
  if (saveInFlight.has(payload.id)) {
    // A save for this project is already running — coalesce into one trailing
    // save with the latest payload instead of starting a concurrent upsert.
    return new Promise<string>((resolve, reject) => {
      const existing = savePending.get(payload.id);
      if (existing) {
        existing.payload = payload;
        existing.waiters.push({ resolve, reject });
      } else {
        savePending.set(payload.id, { payload, waiters: [{ resolve, reject }] });
      }
    });
  }
  return runSave(payload.id, payload);
}

/**
 * Saves or updates a carousel project via the backend API.
 *
 * @param project - Full CarouselProject object
 * @param userId - Authenticated user's ID (kept for interface compat, backend uses token)
 * @returns The saved project's ID
 */
export const saveProject = async (
  project: CarouselProject,
  userId: string
): Promise<string> => {
  // Flatten the project into the format the backend expects
  const payload = {
    id: project.id,
    name: project.name,
    style: project.style,
    aspectRatio: project.aspectRatio,
    profile: project.profile,
    slides: project.slides,
    thumbnailUrl: project.thumbnailUrl || null,
    settings: {
      theme: project.theme,
      accentColor: project.accentColor,
      showAccent: project.showAccent,
      showSlideNumbers: project.showSlideNumbers,
      showVerifiedBadge: project.showVerifiedBadge,
      headerScale: project.headerScale,
      fontStyle: project.fontStyle,
      fontScale: project.fontScale,
      globalImageStyle: project.globalImageStyle,
      layoutSettings: project.layoutSettings,
      nameSource: project.nameSource,
      // Conteúdo Notável metadata
      analisePreCriacao: project.analisePreCriacao,
      checklistValidacao: project.checklistValidacao,
      // Demo flag for onboarding tour projects (excluded from trial count)
      ...(project.isDemo ? { isDemo: true } : {}),
    },
  };

  // Serialized + coalesced so concurrent/overlapping saves of the same project
  // never race the same DB row (see runSave/enqueueSave above).
  return enqueueSave(payload);
};

// ============================================================================
// LOAD PROJECT
// ============================================================================

/**
 * Loads a single project from the backend API by ID.
 * Returns a full CarouselProject object ready for the editor.
 */
export const loadProject = async (projectId: string): Promise<CarouselProject> => {
  const data = await apiPost<{
    id: string;
    name: string;
    style: string;
    aspectRatio: string;
    profile: Record<string, any>;
    slides: Record<string, any>[];
    settings: Record<string, any>;
    thumbnailUrl?: string;
    createdAt: string;
    updatedAt: string;
  }>('/api/projects/load', { projectId });

  const settings = data.settings || {};

  return {
    id: data.id,
    name: data.name,
    style: data.style as CarouselStyle,
    aspectRatio: data.aspectRatio as AspectRatio,
    profile: data.profile as Profile,
    slides: data.slides as unknown as Slide[],
    theme: (settings.theme as Theme) || 'LIGHT',
    accentColor: settings.accentColor || '#4763CA',
    showAccent: settings.showAccent ?? true,
    showSlideNumbers: settings.showSlideNumbers ?? true,
    showVerifiedBadge: settings.showVerifiedBadge ?? true,
    headerScale: settings.headerScale || 1.0,
    fontStyle: (settings.fontStyle as FontStyle) || 'MODERN',
    fontScale: settings.fontScale || 1.0,
    globalImageStyle: settings.globalImageStyle || '',
    layoutSettings: settings.layoutSettings as LayoutSettings | undefined,
    nameSource: settings.nameSource as CarouselProject['nameSource'] | undefined,
    // Conteúdo Notável metadata
    analisePreCriacao: settings.analisePreCriacao as AnalisePreCriacao | undefined,
    checklistValidacao: settings.checklistValidacao as ChecklistValidacao | undefined,
    thumbnailUrl: data.thumbnailUrl,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

// ============================================================================
// LIST PROJECTS
// ============================================================================

/**
 * Lists all projects for the authenticated user via the backend API.
 * Returns summary objects (without full slide data) for the dashboard.
 */
export const listProjects = async (): Promise<ProjectSummary[]> => {
  return await apiGet<ProjectSummary[]>('/api/projects');
};

// ============================================================================
// DELETE PROJECT
// ============================================================================

/**
 * Deletes a project via the backend API.
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  await apiPost('/api/projects/delete', { projectId });
};

// ============================================================================
// DUPLICATE PROJECT
// ============================================================================

/**
 * Duplicates a project via the backend API.
 */
export const duplicateProject = async (
  projectId: string,
  userId: string
): Promise<string> => {
  const data = await apiPost<{ id: string }>('/api/projects/duplicate', { projectId });
  return data.id;
};

// ============================================================================
// RENAME PROJECT
// ============================================================================

/**
 * Renames a project via the backend API.
 */
export const renameProject = async (
  projectId: string,
  name: string
): Promise<void> => {
  await apiPost('/api/projects/rename', { projectId, name });
};
